import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { API_BASE } from '../config/api';
import { 
  Bell, Filter, Search, ChevronDown, ExternalLink, Clock, 
  AlertTriangle, Shield, Briefcase, Lock, Ban, Server,
  Heart, Users, Leaf, Globe, Calendar, Eye, ArrowRight,
  CheckCircle2, X, Loader2, BookOpen, Zap, Crown, Building2,
  RefreshCw, Database, Sparkles, CreditCard, Star, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import NotificationCenter from '../components/NotificationCenter';
const API = API_BASE;

// Category icons
const CATEGORY_ICONS = {
  'aml': Shield,
  'grc': Briefcase,
  'data-privacy': Lock,
  'financial-crime': AlertTriangle,
  'sanctions': Ban,
  'cybersecurity': Shield,
  'it-governance': Server,
  'conduct': Heart,
  'consumer-protection': Users,
  'esg': Leaf
};

// Severity colors
const SEVERITY_STYLES = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  informational: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
};

// Jurisdiction flags
const JURISDICTION_FLAGS = {
  'ZA': '🇿🇦',
  'UK': '🇬🇧',
  'EU': '🇪🇺',
  'INTL': '🌍'
};

function AlertCard({ alert, onClick }) {
  const CategoryIcon = CATEGORY_ICONS[alert.categories?.[0]] || Bell;
  
  return (
    <article 
      onClick={onClick}
      className="bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-amber-500/30 transition-all duration-300 cursor-pointer group overflow-hidden"
      data-testid={`alert-card-${alert.alert_id}`}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${alert.severity === 'critical' || alert.severity === 'high' ? 'bg-red-500/20' : 'bg-amber-500/20'}`}>
              <CategoryIcon className={`h-5 w-5 ${alert.severity === 'critical' || alert.severity === 'high' ? 'text-red-400' : 'text-amber-400'}`} />
            </div>
            <div>
              <span className="text-2xl mr-2">{JURISDICTION_FLAGS[alert.jurisdiction] || '🌍'}</span>
              <span className="text-xs text-slate-500">{alert.regulator_name || alert.regulator_id?.toUpperCase()}</span>
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.medium}`}>
            {alert.severity}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-amber-400 transition-colors line-clamp-2">
          {alert.title}
        </h3>

        {/* Summary */}
        <p className="text-sm text-slate-400 mb-4 line-clamp-2">
          {alert.summary}
        </p>

        {/* Meta */}
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {alert.published_date ? new Date(alert.published_date).toLocaleDateString('en-GB', { 
                day: 'numeric', month: 'short', year: 'numeric' 
              }) : 'Draft'}
            </span>
            {alert.views > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {alert.views}
              </span>
            )}
          </div>
          <ArrowRight className="h-4 w-4 text-slate-600 group-hover:text-amber-400 transition-colors" />
        </div>
      </div>

      {/* Categories */}
      {alert.categories?.length > 0 && (
        <div className="px-5 pb-4 flex flex-wrap gap-2">
          {alert.categories.slice(0, 3).map(cat => (
            <span key={cat} className="px-2 py-0.5 rounded bg-slate-700/50 text-xs text-slate-400">
              {cat}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

function AlertDetailModal({ alert, onClose }) {
  const CategoryIcon = CATEGORY_ICONS[alert.categories?.[0]] || Bell;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${alert.severity === 'critical' || alert.severity === 'high' ? 'bg-red-500/20' : 'bg-amber-500/20'}`}>
                <CategoryIcon className={`h-6 w-6 ${alert.severity === 'critical' || alert.severity === 'high' ? 'text-red-400' : 'text-amber-400'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">{JURISDICTION_FLAGS[alert.jurisdiction] || '🌍'}</span>
                  <span className="text-sm text-slate-400">{alert.regulator_name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${SEVERITY_STYLES[alert.severity]}`}>
                    {alert.severity}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-white">{alert.title}</h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Meta info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <div className="text-xs text-slate-500 mb-1">Published</div>
              <div className="text-sm text-white">
                {alert.published_date ? new Date(alert.published_date).toLocaleDateString('en-GB', { 
                  day: 'numeric', month: 'short', year: 'numeric' 
                }) : 'Not published'}
              </div>
            </div>
            {alert.effective_date && (
              <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                <div className="text-xs text-slate-500 mb-1">Effective Date</div>
                <div className="text-sm text-white">
                  {new Date(alert.effective_date).toLocaleDateString('en-GB', { 
                    day: 'numeric', month: 'short', year: 'numeric' 
                  })}
                </div>
              </div>
            )}
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <div className="text-xs text-slate-500 mb-1">Jurisdiction</div>
              <div className="text-sm text-white">{JURISDICTION_FLAGS[alert.jurisdiction]} {alert.jurisdiction}</div>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <div className="text-xs text-slate-500 mb-1">Views</div>
              <div className="text-sm text-white">{alert.views || 0}</div>
            </div>
          </div>

          {/* Summary */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-amber-400 mb-2">Summary</h3>
            <p className="text-slate-300">{alert.summary}</p>
          </div>

          {/* Full Content */}
          {alert.content && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-amber-400 mb-2">Details</h3>
              <div 
                className="prose prose-invert prose-sm max-w-none text-slate-300"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(alert.content) }}
              />
            </div>
          )}

          {/* AI Impact Analysis */}
          {alert.ai_impact_analysis && (
            <div className="mb-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
              <h3 className="text-sm font-medium text-purple-400 mb-2 flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Impact Analysis
              </h3>
              <p className="text-slate-300 text-sm">{alert.ai_impact_analysis}</p>
            </div>
          )}

          {/* Categories */}
          {alert.categories?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-amber-400 mb-2">Categories</h3>
              <div className="flex flex-wrap gap-2">
                {alert.categories.map(cat => (
                  <span key={cat} className="px-3 py-1 rounded-full bg-slate-700/50 text-sm text-slate-300">
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Related Courses */}
          {alert.related_courses?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Related Training
              </h3>
              <div className="space-y-2">
                {alert.related_courses.map((course, i) => (
                  <a 
                    key={i}
                    href={course.url || '#'}
                    className="block p-3 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-amber-500/30 transition-colors"
                  >
                    <div className="text-white font-medium">{course.title}</div>
                    {course.description && (
                      <div className="text-xs text-slate-400 mt-1">{course.description}</div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Source */}
          {alert.source_url && (
            <div className="pt-4 border-t border-slate-800">
              <a 
                href={alert.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300"
              >
                <ExternalLink className="h-4 w-4" />
                View Original Source
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Subscription tier component
function SubscriptionTiers({ tiers, onSubscribe }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {tiers.map((tier) => (
        <div 
          key={tier.id}
          className={`relative rounded-2xl border p-6 transition-all duration-300 ${
            tier.id === 'professional' 
              ? 'border-amber-500/50 bg-gradient-to-b from-amber-500/10 to-slate-800/50 shadow-lg shadow-amber-500/10' 
              : 'border-slate-700/50 bg-slate-800/50 hover:border-slate-600'
          }`}
        >
          {tier.id === 'professional' && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="px-3 py-1 bg-amber-500 text-slate-900 text-xs font-bold rounded-full">
                MOST POPULAR
              </span>
            </div>
          )}
          
          <div className="text-center mb-6">
            <div className={`inline-flex p-3 rounded-xl mb-4 ${
              tier.id === 'free' ? 'bg-slate-700/50' :
              tier.id === 'professional' ? 'bg-amber-500/20' : 'bg-purple-500/20'
            }`}>
              {tier.id === 'free' ? <Users className="h-6 w-6 text-slate-400" /> :
               tier.id === 'professional' ? <Crown className="h-6 w-6 text-amber-400" /> :
               <Building2 className="h-6 w-6 text-purple-400" />}
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-3xl font-bold text-white">
                {tier.price_monthly === 0 ? 'Free' : `R${tier.price_monthly}`}
              </span>
              {tier.price_monthly > 0 && (
                <span className="text-slate-400 text-sm">/month</span>
              )}
            </div>
            {tier.price_yearly > 0 && (
              <p className="text-xs text-slate-500 mt-1">
                or R{tier.price_yearly}/year (save 17%)
              </p>
            )}
          </div>
          
          <ul className="space-y-3 mb-6">
            {tier.features.map((feature, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                  tier.id === 'professional' ? 'text-amber-400' : 'text-emerald-400'
                }`} />
                <span className="text-sm text-slate-300">{feature}</span>
              </li>
            ))}
          </ul>
          
          <Button
            onClick={() => onSubscribe(tier.id)}
            className={`w-full ${
              tier.id === 'professional' 
                ? 'bg-amber-500 hover:bg-amber-600 text-slate-900'
                : tier.id === 'free'
                ? 'bg-slate-700 hover:bg-slate-600 text-white'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
            data-testid={`subscribe-${tier.id}-btn`}
          >
            {tier.id === 'free' ? 'Get Started' : 'Subscribe'}
          </Button>
        </div>
      ))}
    </div>
  );
}

// Admin controls component
function AdminControls({ onSeedAlerts, onRunScraper, loading }) {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-purple-500/20">
          <Sparkles className="h-5 w-5 text-purple-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Admin Controls</h3>
          <p className="text-sm text-slate-400">Manage regulatory alerts data</p>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-4">
        <Button
          onClick={onSeedAlerts}
          disabled={loading}
          variant="outline"
          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          data-testid="seed-alerts-btn"
        >
          <Database className="h-4 w-4 mr-2" />
          Seed Known Alerts
        </Button>
        
        <Button
          onClick={onRunScraper}
          disabled={loading}
          variant="outline"
          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
          data-testid="run-scraper-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Run Scraper
        </Button>
      </div>
      
      <p className="text-xs text-slate-500 mt-4">
        * Seed alerts loads known regulatory data. Scraper fetches live data from regulator websites.
      </p>
    </div>
  );
}

export default function RegulatoryAlertsPage() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jurisdictions, setJurisdictions] = useState({});
  const [categories, setCategories] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Modal
  const [selectedAlert, setSelectedAlert] = useState(null);

  useEffect(() => {
    fetchJurisdictions();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [searchTerm, selectedJurisdiction, selectedCategory, selectedSeverity, skip]);

  const fetchJurisdictions = async () => {
    try {
      const response = await axios.get(`${API}/api/regulatory/jurisdictions`);
      setJurisdictions(response.data.jurisdictions || {});
    } catch (error) {
      console.error('Failed to fetch jurisdictions:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/regulatory/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedJurisdiction !== 'all') params.append('jurisdiction', selectedJurisdiction);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedSeverity !== 'all') params.append('severity', selectedSeverity);
      if (searchTerm) params.append('search', searchTerm);
      params.append('skip', skip.toString());
      params.append('limit', '20');

      const response = await axios.get(`${API}/api/regulatory/alerts?${params}`);
      
      if (skip === 0) {
        setAlerts(response.data.alerts || []);
      } else {
        setAlerts(prev => [...prev, ...(response.data.alerts || [])]);
      }
      setTotal(response.data.total || 0);
      setHasMore(response.data.has_more || false);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to load regulatory alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleAlertClick = async (alert) => {
    try {
      const response = await axios.get(`${API}/api/regulatory/alerts/${alert.alert_id}`);
      setSelectedAlert(response.data);
    } catch (error) {
      console.error('Failed to fetch alert details:', error);
      setSelectedAlert(alert);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedJurisdiction('all');
    setSelectedCategory('all');
    setSelectedSeverity('all');
    setSkip(0);
  };

  const hasActiveFilters = searchTerm || selectedJurisdiction !== 'all' || selectedCategory !== 'all' || selectedSeverity !== 'all';

  // Admin state
  const [adminLoading, setAdminLoading] = useState(false);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [showSubscription, setShowSubscription] = useState(false);
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Fetch subscription tiers
  useEffect(() => {
    fetchSubscriptionTiers();
  }, []);

  const fetchSubscriptionTiers = async () => {
    try {
      const response = await axios.get(`${API}/api/regulatory/subscription/tiers`);
      setSubscriptionTiers(response.data.tiers || []);
    } catch (error) {
      console.error('Failed to fetch subscription tiers:', error);
    }
  };

  const handleSeedAlerts = async () => {
    setAdminLoading(true);
    try {
      const response = await axios.post(`${API}/api/regulatory/admin/seed-alerts`);
      toast.success(`Seeded ${response.data.inserted} regulatory alerts`);
      setSkip(0);
      fetchAlerts();
    } catch (error) {
      console.error('Failed to seed alerts:', error);
      toast.error('Failed to seed alerts');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleRunScraper = async () => {
    setAdminLoading(true);
    toast.info('Running regulatory scraper... This may take a moment.');
    try {
      const response = await axios.post(`${API}/api/regulatory/admin/run-scraper`);
      const stats = response.data.stats;
      toast.success(`Scraper complete: ${stats.inserted} new alerts (${stats.duplicates} duplicates skipped)`);
      setSkip(0);
      fetchAlerts();
    } catch (error) {
      console.error('Failed to run scraper:', error);
      toast.error('Failed to run scraper');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleSubscribe = async (tierId) => {
    if (tierId === 'free') {
      toast.success('You are now using the free tier!');
      setShowSubscription(false);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/regulatory/subscription/checkout?tier_id=${tierId}&billing_cycle=monthly`);
      if (response.data.status === 'placeholder') {
        toast.info('Stripe integration coming soon! Subscription placeholders are in place.');
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      toast.error('Failed to start checkout');
    }
  };

  return (
    <>
      <Helmet>
        <title>Regulatory Alerts | Navigate Learning</title>
        <meta name="description" content="Stay updated with the latest regulatory changes affecting compliance, AML, data privacy, and governance across South Africa, UK, EU and international bodies." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        {/* Navigation */}
        <nav className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-md sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                  <span className="text-slate-900 font-bold text-sm">N</span>
                </div>
                <span className="text-white font-semibold">Navigate Learning</span>
              </Link>
              <div className="flex items-center gap-4">
                <Link to="/academy" className="text-sm text-slate-400 hover:text-white transition-colors">
                  Academy
                </Link>
                <Link to="/courses" className="text-sm text-slate-400 hover:text-white transition-colors">
                  Courses
                </Link>
                {user && <NotificationCenter />}
                {user ? (
                  <Link to="/dashboard" className="text-sm text-amber-400 hover:text-amber-300 transition-colors">
                    Dashboard
                  </Link>
                ) : (
                  <Link to="/login">
                    <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                      Sign In
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-6">
                <Bell className="h-4 w-4" />
                Regulatory Intelligence
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                Regulatory <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Alerts</span>
              </h1>
              <p className="text-lg text-slate-400">
                Stay ahead of regulatory changes affecting your compliance obligations. 
                Real-time updates from regulators across South Africa, UK, EU and international bodies.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Admin Controls */}
            {isAdmin && (
              <AdminControls 
                onSeedAlerts={handleSeedAlerts}
                onRunScraper={handleRunScraper}
                loading={adminLoading}
              />
            )}

            {/* Subscription CTA */}
            {!showSubscription && (
              <div className="mb-8 p-6 bg-gradient-to-r from-amber-500/10 to-purple-500/10 rounded-2xl border border-amber-500/20">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-amber-500/20">
                      <Zap className="h-6 w-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Unlock Premium Alerts</h3>
                      <p className="text-sm text-slate-400">Get real-time notifications, AI analysis, and multi-jurisdiction coverage</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setShowSubscription(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                    data-testid="view-plans-btn"
                  >
                    <Crown className="h-4 w-4 mr-2" />
                    View Plans
                  </Button>
                </div>
              </div>
            )}

            {/* Subscription Tiers */}
            {showSubscription && subscriptionTiers.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Choose Your Plan</h2>
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowSubscription(false)}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="h-4 w-4 mr-1" /> Close
                  </Button>
                </div>
                <SubscriptionTiers tiers={subscriptionTiers} onSubscribe={handleSubscribe} />
                <p className="text-center text-sm text-slate-500 mt-6">
                  <CreditCard className="h-4 w-4 inline mr-1" />
                  Secure payments powered by Stripe (coming soon)
                </p>
              </div>
            )}

            {/* Search & Filters */}
            <div className="mb-8 space-y-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                  <Input
                    placeholder="Search regulatory alerts..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setSkip(0); }}
                    className="pl-12 h-12 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="h-12 border-slate-700 text-white"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </Button>
              </div>

              {showFilters && (
                <div className="flex flex-wrap gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Jurisdiction</label>
                    <select
                      value={selectedJurisdiction}
                      onChange={(e) => { setSelectedJurisdiction(e.target.value); setSkip(0); }}
                      className="h-10 px-3 rounded-lg bg-slate-700 border-slate-600 text-white text-sm min-w-[150px]"
                    >
                      <option value="all">All Jurisdictions</option>
                      {Object.entries(jurisdictions).map(([code, j]) => (
                        <option key={code} value={code}>{j.flag} {j.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Category</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => { setSelectedCategory(e.target.value); setSkip(0); }}
                      className="h-10 px-3 rounded-lg bg-slate-700 border-slate-600 text-white text-sm min-w-[180px]"
                    >
                      <option value="all">All Categories</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Severity</label>
                    <select
                      value={selectedSeverity}
                      onChange={(e) => { setSelectedSeverity(e.target.value); setSkip(0); }}
                      className="h-10 px-3 rounded-lg bg-slate-700 border-slate-600 text-white text-sm min-w-[140px]"
                    >
                      <option value="all">All Severities</option>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                      <option value="informational">Informational</option>
                    </select>
                  </div>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      onClick={clearFilters}
                      className="h-10 text-slate-400 hover:text-white self-end"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear filters
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Results count */}
            <div className="text-sm text-slate-400 mb-6">
              {loading ? 'Loading...' : `Showing ${alerts.length} of ${total} alerts`}
            </div>

            {/* Alerts Grid */}
            {loading && alerts.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
              </div>
            ) : alerts.length === 0 ? (
              <div className="text-center py-20 bg-slate-800/50 rounded-xl border border-slate-700">
                <Bell className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No alerts found</h3>
                <p className="text-slate-400 mb-4">Try adjusting your search or filters</p>
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters} className="border-slate-700 text-white">
                    Clear all filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {alerts.map(alert => (
                    <AlertCard 
                      key={alert.alert_id} 
                      alert={alert} 
                      onClick={() => handleAlertClick(alert)}
                    />
                  ))}
                </div>

                {/* Load More */}
                {hasMore && (
                  <div className="text-center mt-8">
                    <Button
                      variant="outline"
                      onClick={() => setSkip(prev => prev + 20)}
                      disabled={loading}
                      className="border-slate-700 text-white"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Load More'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <AlertDetailModal 
          alert={selectedAlert} 
          onClose={() => setSelectedAlert(null)} 
        />
      )}
    </>
  );
}
